import React, { useRef, useState, useMemo, useEffect } from 'react';
import './CMComposer.scss';
import { IconRTEAttachment, IconEmoji } from 'components/Icons';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { useClickAway } from 'react-use';
import classNames from 'classnames';
import { useReopenFlaggedTransactionMutation } from '../../../../hooks/flaggedTransactions';
import { useParams } from 'react-router-dom';
import { useCreateQueueCommentMutation } from 'hooks/queue/useCreateQueueCommentMutation';
import { AskRequest } from '@hone-automation/common';
import { useAuthContext } from '../../../../context/useAuthContext';
import { useQueueContext } from '../QueueContext';
import RichTextEditor, { RichTextEditorRef } from './RichTextEditor/RichTextEditor';
import { convertToShortcodes } from '../utils/emojiParser';
import { useAIQuery } from 'presentation/components/KAI/KAI.hook';
import { Tooltip, Whisper } from 'rsuite';
import KSCheckBox from 'presentation/components/KSCheckBox';
import AutoEnhancer from './AutoEnhancer';
import AttachmentsContainer, { AttachmentItemType } from './Attachments/AttachmentContainer/AttachmentsContainer';
import { UploadProgress } from './Attachments/AttachmentItem/AttachmentsItem';

interface MessageComposerProps {
  /** The Ask request being responded to */
  request: AskRequest;
  /** Whether to enable KAI (AI assistance) features */
  KAI?: boolean;
}

/**
 * Message composer component for creating and sending responses to customer requests.
 * Features rich text editing, emoji insertion, and optional AI assistance for message enhancement.
 *
 * @param {MessageComposerProps} props - The component props
 * @param {AskRequest} props.request - The Ask request being responded to
 * @param {boolean} [props.KAI=false] - Whether to enable KAI (AI assistance) features
 * @returns {JSX.Element} The rendered message composer
 */
const MessageComposer = ({ request, KAI = false }: MessageComposerProps) => {
  const { locationId } = useParams();

  const {
    dispatch,
    state: { selectedItem, requests },
  } = useQueueContext();

  const { user } = useAuthContext();
  const {
    mutate: createCommentMutation,
    isPending,
    isSuccess,
  } = useCreateQueueCommentMutation(request.status, locationId!, trx => {
    const trxExists = requests.some(req => req.id === trx?.id);

    if (trxExists) {
      // Replace the existing trx
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === trx?.id ? (trx as unknown as AskRequest) : req)),
      });
    } else {
      // Add trx as a new request
      dispatch({
        type: 'SET_REQUESTS',
        payload: [...requests, trx as unknown as AskRequest],
      });
    }
    dispatch({ type: 'SET_SELECTED_ITEM', payload: trx as unknown as AskRequest });
  });

  const postReopenMutation = useReopenFlaggedTransactionMutation(request.status, locationId!, response => {
    //
  });

  const [toolbarOpen, setToolbar] = useState<boolean>(true);
  const [emojiPickerOpen, setEmojiPicker] = useState<boolean>(false);
  const [richText, setRichText] = useState<string>('');
  const [text, setText] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    percentage: 0,
    status: 'pending',
  });
  const [attachments, setAttachments] = useState<AttachmentItemType[]>([]);
  const [KAIclicked, setKAIclicked] = useState<boolean>(false);
  const [autoEnhance, setAutoEnhance] = useState<boolean>(false);

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const richTextEditorRef = useRef<RichTextEditorRef>(null);
  const { sendQuery } = useAIQuery();

  const handleEmojiRTEClick = (emojiData: EmojiClickData) => {
    if (richTextEditorRef.current) {
      richTextEditorRef.current.insertText(emojiData.emoji);
    }
    setEmojiPicker(false);

    // Use a small timeout to ensure focus returns to the editor
    setTimeout(() => {
      if (richTextEditorRef.current) {
        richTextEditorRef.current.focus();
      }
    }, 10);
  };

  const toggleEmojiPick = () => {
    setEmojiPicker(prevState => {
      return !prevState;
    });
  };

  useClickAway(emojiPickerRef, () => {
    setEmojiPicker(false);
  });

  const handleRewrite = (message: string) => {
    // Send a query to the AI
    if (richTextEditorRef.current) {
      richTextEditorRef.current.KAI(message);
    }
  };
  const handleSubmit = () => {
    let filesReadCount = 0; // Track the number of files read
    const formData = new FormData();

    // Check if both plain text and rich text are empty
    if (text === '' || richText === '' || richText === '<br>') return;

    const emojiSafeRichText = convertToShortcodes(richText);
    const emojiSafeText = convertToShortcodes(text);

    if (selectedItem?.status === 'Closed' && selectedItem.canReopen) {
      postReopenMutation.mutate({
        trxId: selectedItem?.id,
        comment: emojiSafeRichText, // Use rich text if you want to preserve formatting
      });
    }

    // Append regular fields
    formData.append('comment', emojiSafeRichText);
    formData.append('userId', user?.uid as string);
    formData.append('locationId', locationId as string);
    formData.append('activeTransaction', JSON.stringify(request)); // serialize if it's an object

    const resetFields = () => {
      setRichText('');
      setText('');
      setFile(null);
      setAttachments([]);
    };

    // send without att
    if (attachments.length === 0) {
      createCommentMutation(formData);
      if (richTextEditorRef.current) richTextEditorRef.current.submit();
      resetFields();
      return;
    }

    // Function to handle file reading and mutation
    const processFiles = () => {
      if (filesReadCount === attachments.length) {
        createCommentMutation(formData);

        // Reset both states
        if (richTextEditorRef.current) richTextEditorRef.current.submit();
        resetFields();
      }
    };

    // Append files and read as data URL
    attachments.forEach((file, index) => {
      filesReadCount++;
      formData.append(`attachment`, file.file as File);
      processFiles(); // Check if all files are processed
    });
  };

  const handleRichTextChange = (html: string, plain: string) => {
    //console.log('Rich Text:', html, text);
    setRichText(html);
    setText(plain);
  };

  const hideActions = false;
  const hideAttachments = true;

  // Memoize the RichTextEditor component
  const MemoizedRichTextEditor = useMemo(
    () => (
      <RichTextEditor
        ref={richTextEditorRef}
        value={richText}
        onChange={handleRichTextChange}
        onSubmit={handleSubmit}
        autoFocus
        className={`message-input ${toolbarOpen ? 'tooltip-open' : ''}`}
        maxHeight="150px"
        toolbar={toolbarOpen}
      />
    ),
    [richText, toolbarOpen, handleSubmit]
  );

  const handleOpenUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef?.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };

  const simulateFileUpload = (fileToUpload: File) => {
    setUploadProgress({ percentage: 0, status: 'uploading' });

    // Simulate network latency
    const latency = 1000; // Adjust for desired latency in milliseconds
    let uploadedBytes = 0;
    const totalBytes = fileToUpload.size;
    const chunkSize = 1024 * 100; // 100KB chunks
    let intervalId: NodeJS.Timeout | null = null;

    const uploadChunk = () => {
      if (uploadProgress.status === 'error') {
        if (intervalId) clearInterval(intervalId);
        return;
      }

      uploadedBytes += chunkSize;
      const newPercentage = Math.min(100, Math.round((uploadedBytes / totalBytes) * 100));
      setUploadProgress(prevProgress => ({ ...prevProgress, percentage: newPercentage }));

      if (uploadedBytes >= totalBytes) {
        if (intervalId) clearInterval(intervalId);
        // Simulate a successful upload after a short delay
        setTimeout(() => {
          setUploadProgress({ percentage: 100, status: 'success' });
        }, 500);
      }
    };

    // Start the simulated upload interval
    intervalId = setInterval(uploadChunk, latency / 10); // Update progress every latency/10 ms
  };

  useEffect(() => {
    if (uploadProgress) {
      const updatedAttachments = attachments.map(attachment => {
        if (attachment.id === attachments.length) {
          return { ...attachment, processing: uploadProgress };
        }
        return attachment;
      });
      setAttachments(updatedAttachments);
    }
  }, [uploadProgress]);

  useEffect(() => {
    if (file) {
      const newAttachment = {
        id: attachments.length + 1,
        type: file.type.split('/')[1],
        name: file.name,
        file: file,
      };
      simulateFileUpload(file);
      setAttachments([...attachments, newAttachment]);
    }
  }, [file]);

  const handleDeleteAttachment = (id: number) => {
    setFile(null);
    setAttachments(attachments.filter(attachment => attachment.id !== id));
  };

  return (
    <div className="message-composer">
      {KAI && <AutoEnhancer message={text} rewriteMessageCallBack={handleRewrite} toolbarOpen={toolbarOpen} />}
      <div className="message-row">{MemoizedRichTextEditor}</div>
      <input
        id="file-upload"
        type="file"
        accept="*"
        className="hidden"
        onChange={handleFileChange}
        ref={fileInputRef}
      />
      {!!attachments.length && <AttachmentsContainer attachments={attachments} removeFile={handleDeleteAttachment} />}

      <div className="message-actions">
        <div className="left-actions">
          {!hideActions && (
            <>
              <button
                className={classNames('action-button', { active: toolbarOpen })}
                onClick={() => setToolbar(prevValue => !prevValue)}
              >
                A<span>a</span>
              </button>
              <button
                onClick={handleOpenUploadFile}
                className={classNames('action-button', { active: emojiPickerOpen })}
              >
                <IconRTEAttachment />
              </button>
              <button onClick={toggleEmojiPick} className={classNames('action-button', { active: emojiPickerOpen })}>
                <IconEmoji />
              </button>

              <div ref={emojiPickerRef} className="emoji-wrapper">
                {/* @ts-ignore: Types from package are wrong */}
                <EmojiPicker open={emojiPickerOpen} onEmojiClick={handleEmojiRTEClick} /> {/* handleEmojiClick */}
              </div>
            </>
          )}
        </div>

        <div className="right-actions">
          {KAI && (
            <Whisper
              placement="top"
              trigger="hover"
              delayOpen={1200}
              speaker={<Tooltip>Automatically enhance the message when it's sent</Tooltip>}
            >
              <div className="message-auto-improve">
                <KSCheckBox
                  rounded={true}
                  smallCaps={true}
                  label={'Auto-Enhance message'}
                  checked={autoEnhance}
                  onChange={function (checked: boolean): void {
                    setAutoEnhance(checked);
                  }}
                />
              </div>
            </Whisper>
          )}
          <button
            className={`message-submit${text.length > 0 ? '' : ' disabled'}`}
            onClick={handleSubmit}
            disabled={isPending}
          >
            SEND
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;
